import React, { useState } from 'react';
import axios from 'axios';
import AsyncSelect from 'react-select/async';
import { components } from 'react-select';
import { Prod01 } from '../../constants/ActionType';

/**
 * ICD-10-CM multi-select: search by code or by name; chips show the code (description on hover).
 * value / onChange: [{ code, description }]. Free text is never stored.
 * legacyText: the old free-text diseases value, shown read-only with a "Match to ICD-10" helper.
 */
const MultiValueLabel = (props) => (
    <span title={props.data.description} data-testid={`icd-chip-${props.data.code}`}>
        <components.MultiValueLabel {...props} />
    </span>
);

export const loadIcd10 = async (q) => {
    if (!q || q.trim().length < 2) return [];
    const { data } = await axios.get(`${Prod01}/patient/form/icd10`, { params: { q } });
    return (data?.results || []).map((r) => ({ ...r, value: r.code, label: r.code }));
};

const Icd10Picker = ({ value = [], onChange, legacyText, error, label = 'Diagnoses (ICD-10-CM)', compulsory, inputId = 'icd10-picker' }) => {
    const [matches, setMatches] = useState(null);
    const [matching, setMatching] = useState(false);
    const selected = value.map((d) => ({ ...d, value: d.code, label: d.code }));
    const add = (d) => {
        if (!value.some((v) => v.code === d.code)) onChange([...value, { code: d.code, description: d.description }]);
    };
    const runMatch = async () => {
        setMatching(true);
        try {
            const { data } = await axios.get(`${Prod01}/patient/form/icd10/match`, { params: { text: legacyText } });
            setMatches(data || []);
        } catch (e) {
            setMatches([]);
        }
        setMatching(false);
    };
    return (
        <div className="mb-2">
            <label htmlFor={inputId} className="form-label mt-3">
                {label} {compulsory ? <span style={{ color: 'red', fontWeight: 'bold' }}> *</span> : null}
            </label>
            <AsyncSelect
                inputId={inputId}
                isMulti
                cacheOptions
                defaultOptions={false}
                loadOptions={loadIcd10}
                value={selected}
                components={{ MultiValueLabel }}
                formatOptionLabel={(o, { context }) => (context === 'menu' ? `${o.code} — ${o.description}` : o.code)}
                onChange={(opts) => onChange((opts || []).map((o) => ({ code: o.code, description: o.description })))}
                placeholder="Search code (E78.2) or name (hyperlipidemia)…"
                noOptionsMessage={({ inputValue }) => (inputValue.length < 2 ? 'Type at least 2 characters' : 'No ICD-10-CM match')}
            />
            {error ? <div className="error">{error}</div> : null}
            {legacyText ? (
                <div className="mt-1 p-2" style={{ background: '#f7f7f7', borderRadius: 6, fontSize: 13 }} data-testid="legacy-diseases">
                    <strong>Legacy (free text):</strong> <span>{legacyText}</span>{' '}
                    <button type="button" className="btn btn-link btn-sm p-0 ml-2" onClick={runMatch} disabled={matching}>
                        {matching ? 'Matching…' : 'Match to ICD-10'}
                    </button>
                    {matches && (
                        <ul className="mb-0 mt-1 pl-3">
                            {matches.map((m) => (
                                <li key={m.text}>
                                    “{m.text}” →{' '}
                                    {m.suggestions.length ? m.suggestions.map((s) => (
                                        <button
                                            type="button"
                                            key={s.code}
                                            title={s.description}
                                            className="btn btn-outline-primary btn-sm py-0 px-1 mr-1"
                                            disabled={value.some((v) => v.code === s.code)}
                                            onClick={() => add(s)}
                                        >
                                            {value.some((v) => v.code === s.code) ? '✓ ' : '+ '}{s.code} {s.description}
                                        </button>
                                    )) : <em className="text-muted">no suggestion — search above</em>}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            ) : null}
        </div>
    );
};

export default Icd10Picker;
